import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronRight, Twitter, Linkedin, Facebook, Instagram } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BRAND = '#1a3a5c';
const ACCENT = '#4F46E5';

export default function PublicLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled,   setScrolled]   = useState(false);
  const [isMobile,   setIsMobile]   = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const navLinks = [
    { to: '/',         label: t('nav.home') },
    { to: '/features', label: t('nav.features') },
    { to: '/pricing',  label: t('nav.pricing') },
    { to: '/about',    label: t('nav.about') },
    { to: '/contact',  label: t('nav.contact') },
  ];
  const isAm = i18n.language === 'am';

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  const isActive = (to) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
      color: '#0F172A',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <style>{`
        .pub-nav-link {
          padding: 7px 14px;
          border-radius: 8px;
          text-decoration: none;
          font-size: 14.5px;
          font-weight: 500;
          transition: all 0.15s ease;
          position: relative;
        }
        .pub-nav-link:hover { background: #F1F5F9; color: #0F172A !important; }
        .pub-btn-ghost {
          padding: 8px 18px;
          border: 1.5px solid #CBD5E1;
          border-radius: 8px;
          background: transparent;
          color: #334155;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
          font-family: inherit;
        }
        .pub-btn-ghost:hover { border-color: #94A3B8; background: #F8FAFC; }
        .pub-btn-primary {
          padding: 9px 20px;
          border-radius: 8px;
          background: ${ACCENT};
          border: none;
          color: #fff;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
          font-family: inherit;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .pub-btn-primary:hover { background: #4338CA; transform: translateY(-1px); box-shadow: 0 4px 14px rgba(79,70,229,0.35); }
        .pub-footer-link {
          display: block;
          color: #94A3B8;
          text-decoration: none;
          font-size: 14px;
          margin-bottom: 11px;
          font-weight: 400;
          transition: color 0.15s;
        }
        .pub-footer-link:hover { color: #E2E8F0; }
        .pub-social-btn {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.12);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s ease;
          color: #94A3B8;
        }
        .pub-social-btn:hover { background: rgba(255,255,255,0.15); color: #fff; }
        @media (max-width: 768px) {
          .pub-desktop-nav  { display: none !important; }
          .pub-desktop-cta  { display: none !important; }
          .pub-hamburger    { display: none !important; }
          .pub-mobile-strip { display: flex !important; }
          .pub-footer-grid  { grid-template-columns: 1fr 1fr !important; }
          .pub-nav-inner    { height: auto !important; padding: 0 !important; }
        }
        @media (max-width: 480px) {
          .pub-footer-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── Navbar ── */}
      <header style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        paddingTop: 'env(safe-area-inset-top)',
        background: scrolled ? 'rgba(255,255,255,0.92)' : '#fff',
        backdropFilter: scrolled ? 'blur(24px) saturate(200%)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(24px) saturate(200%)' : 'none',
        boxShadow: scrolled ? '0 1px 0 rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.08)' : '0 1px 0 #F1F5F9',
        transition: 'all 0.25s ease',
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '0 32px',
          height: 90,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }} className="pub-nav-inner">
          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', ...(isMobile ? { flex: 1 } : { flexShrink: 0 }) }}>
            <img src="/addiscore-header.png" alt="AddisCore" style={isMobile ? { width: '100%', height: 170, objectFit: 'cover', objectPosition: 'center 40%' } : { height: 72, width: 'auto' }} />
          </Link>

          {/* Desktop nav */}
          <nav className="pub-desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {navLinks.map(({ to, label }) => (
              <Link key={to} to={to} className="pub-nav-link" style={{
                color: isActive(to) ? ACCENT : '#475569',
                fontWeight: isActive(to) ? 600 : 500,
                background: isActive(to) ? '#EEF2FF' : 'transparent',
              }}>
                {label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="pub-desktop-cta" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Language toggle */}
            <button
              onClick={() => i18n.changeLanguage(isAm ? 'en' : 'am')}
              style={{
                display: 'flex', alignItems: 'center', gap: 0,
                background: 'transparent', border: '1.5px solid #CBD5E1',
                borderRadius: 20, overflow: 'hidden', cursor: 'pointer', padding: 0,
              }}
            >
              <span style={{ padding: '4px 10px', fontSize: 11, fontWeight: 700, background: !isAm ? ACCENT : 'transparent', color: !isAm ? '#fff' : '#64748B', transition: 'all .15s' }}>EN</span>
              <span style={{ padding: '4px 10px', fontSize: 11, fontWeight: 700, background: isAm ? ACCENT : 'transparent', color: isAm ? '#fff' : '#64748B', transition: 'all .15s' }}>አማ</span>
            </button>
            <button className="pub-btn-ghost" onClick={() => navigate('/login')}>{t('nav.login')}</button>
            <button className="pub-btn-primary" onClick={() => navigate('/request-demo')}>
              {t('nav.requestDemo')} <ChevronRight size={14} />
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="pub-hamburger"
            onClick={() => setMobileOpen(o => !o)}
            style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: '#475569' }}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile nav strip — visible on small screens instead of hamburger */}
        <div className="pub-mobile-strip" style={{
          display: 'none',
          alignItems: 'center',
          borderTop: '1px solid #F1F5F9',
          padding: '0 8px',
          background: '#fff',
          overflowX: 'auto',
          gap: 0,
        }}>
          {[
            { to: '/',         label: t('nav.home') },
            { to: '/features', label: t('nav.features') },
            { to: '/pricing',  label: t('nav.pricing') },
          ].map(({ to, label }) => (
            <Link key={to} to={to} style={{
              padding: '10px 14px',
              fontSize: 13,
              fontWeight: isActive(to) ? 700 : 500,
              color: isActive(to) ? ACCENT : '#475569',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              borderBottom: `2px solid ${isActive(to) ? ACCENT : 'transparent'}`,
              flexShrink: 0,
            }}>
              {label}
            </Link>
          ))}
          <button onClick={() => navigate('/login')} style={{
            marginLeft: 'auto',
            padding: '7px 18px',
            fontSize: 13,
            fontWeight: 700,
            color: '#fff',
            background: ACCENT,
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontFamily: 'inherit',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>
            {t('nav.login')}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div style={{
            background: '#fff',
            borderTop: '1px solid #F1F5F9',
            padding: '8px 16px 20px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          }}>
            {navLinks.map(({ to, label }) => (
              <Link key={to} to={to} style={{
                display: 'block',
                padding: '12px 8px',
                textDecoration: 'none',
                fontSize: 15,
                fontWeight: isActive(to) ? 700 : 500,
                color: isActive(to) ? ACCENT : '#334155',
                borderBottom: '1px solid #F8FAFC',
              }}>
                {label}
              </Link>
            ))}
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={() => i18n.changeLanguage(isAm ? 'en' : 'am')}
                style={{ display: 'flex', alignItems: 'center', border: '1.5px solid #CBD5E1', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', padding: 0, background: 'transparent', width: '100%' }}
              >
                <span style={{ flex: 1, padding: '12px', fontSize: 14, fontWeight: 700, background: !isAm ? ACCENT : 'transparent', color: !isAm ? '#fff' : '#64748B' }}>EN — English</span>
                <span style={{ flex: 1, padding: '12px', fontSize: 14, fontWeight: 700, background: isAm ? ACCENT : 'transparent', color: isAm ? '#fff' : '#64748B' }}>አማ — አማርኛ</span>
              </button>
              <button onClick={() => navigate('/login')} style={{ padding: '12px', border: '1.5px solid #CBD5E1', borderRadius: 10, background: 'transparent', color: '#334155', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                {t('nav.login')}
              </button>
              <button onClick={() => navigate('/request-demo')} style={{ padding: '12px', borderRadius: 10, background: ACCENT, border: 'none', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                {t('nav.requestDemo')}
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Content */}
      <main style={{ flex: 1 }}>{children}</main>

      {/* ── Footer ── */}
      <footer style={{ background: '#0F172A', color: '#fff' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '64px 24px 40px' }}>
          <div className="pub-footer-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr', gap: 48, marginBottom: 48 }}>

            {/* Brand */}
            <div>
              <div style={{ marginBottom: 12 }}>
                <img src="/addiscore-logo.png" alt="AddisCore" style={{ height: 44 }} />
              </div>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 14, letterSpacing: '0.02em' }}>
                {t('nav.tagline')}
              </p>
              <p style={{ fontSize: 14, color: '#64748B', lineHeight: 1.75, margin: '0 0 24px', maxWidth: 260 }}>
                {t('nav.taglineSub')}
              </p>
              {/* Social icons */}
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { Icon: Twitter,   label: 'Twitter'   },
                  { Icon: Linkedin,  label: 'LinkedIn'  },
                  { Icon: Facebook,  label: 'Facebook'  },
                  { Icon: Instagram, label: 'Instagram' },
                ].map(({ Icon, label }) => (
                  <button key={label} className="pub-social-btn" title={label}>
                    <Icon size={15} />
                  </button>
                ))}
              </div>
            </div>

            {/* Platform */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 18 }}>{t('nav.footerPlatform')}</div>
              {[['/', t('nav.home')], ['/features', t('nav.features')], ['/pricing', t('nav.pricing')], ['/about', t('nav.about')], ['/contact', t('nav.contact')]].map(([to, label]) => (
                <Link key={to} to={to} className="pub-footer-link">{label}</Link>
              ))}
            </div>

            {/* Resources */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 18 }}>{t('nav.footerResources')}</div>
              {[
                ['/request-demo', t('nav.requestDemoShort')],
                ['/contact',      t('nav.contactSales')],
                ['/about',        t('nav.ourStory')],
                ['/contact',      t('nav.support')],
              ].map(([to, label]) => (
                <Link key={label} to={to} className="pub-footer-link">{label}</Link>
              ))}
            </div>

            {/* Contact */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 18 }}>{t('nav.footerContact')}</div>
              <div style={{ fontSize: 14, color: '#64748B', marginBottom: 24 }}>contactus@addiscoreet.com</div>
              <button onClick={() => navigate('/request-demo')} style={{ padding: '10px 20px', borderRadius: 8, background: ACCENT, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                {t('nav.requestDemoShort')}
              </button>
            </div>
          </div>

          {/* Bottom bar */}
          <div style={{ borderTop: '1px solid #1E293B', paddingTop: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <span style={{ fontSize: 13, color: '#475569' }}>
              © {new Date().getFullYear()} AddisCore. {t('nav.allRightsReserved')}
            </span>
            <div style={{ display: 'flex', gap: 24 }}>
              <Link to="/privacy-policy" style={{ fontSize: 13, color: '#475569', textDecoration: 'none' }}>{t('nav.privacyPolicy')}</Link>
              <Link to="/contact" style={{ fontSize: 13, color: '#475569', textDecoration: 'none' }}>{t('nav.termsOfService')}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
